import { gql } from '@apollo/client';
import { GetStaticPaths, GetStaticProps } from 'next';
import ProjectPageComponent from '../../components/elements/ProjectPage';
import PageLayout, { MenuProps } from '../../layouts/Page';
import { PROJECT_ITEM } from '../../queries/projects';
import {
  Project,
  ProjectProjectsDynamicDynamicZone,
  Technology,
  Testimony,
  UploadFile,
} from '../../types/schema';
import { initializeApollo } from '../../utils/apollo';
import { Error, sendError } from '../../utils/error';
import { formatSrcSet } from '../../utils/formatURLs';
import transformUrl from '../../utils/transformURL';
import { flatten, orderDataByRichTextId, translateProperty } from '../../utils/object';

type ProjectsProps = {
  project: Project;
  technologies: Technology[];
  testimonies: Testimony[];
  menus: MenuProps;
};

const ProjectPage: React.FC<ProjectsProps> = ({ project, technologies, testimonies, menus }) => {
  return (
    <PageLayout menus={menus} locale="en">
      <ProjectPageComponent
        metaTitle={project?.metadata?.metaTitle as string}
        metaDescription={project?.metadata?.metaDescription as string}
        metaImage={(project.metadata?.metaImage as unknown as { url?: string })?.url as string}
        hideAppMastersTitle={project.metadata?.hideAppMastersTitle as boolean}
        title={project.title as string}
        description={project.description as string}
        slug={project.slug as string}
        projectsDynamic={project.projectsDynamic as ProjectProjectsDynamicDynamicZone[]}
        image={formatSrcSet(project.image as UploadFile, 'projectBanner')}
        seoImage={transformUrl((project.image as UploadFile)?.url, 'projectBanner') as string}
        technologies={technologies}
        testimonies={testimonies}
        createdAt={project.createdAt}
        updatedAt={project.updatedAt}
      />
    </PageLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const apolloClient = initializeApollo();
  const { data } = await apolloClient.query({
    query: gql`
      query GET_PROJECTS {
        projects(locale: "en") {
          slug
        }
      }
    `,
  });

  const newData = flatten(data) as { projects: Project[] };

  const paths = newData.projects.map((project: Project) => {
    return {
      params: { slug: project.slug as string },
      locale: 'en',
    };
  });
  return {
    paths: paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { slug } = ctx.params as { slug: string };
  const { locale } = ctx;
  try {
    const apolloClient = initializeApollo();
    const { data } = await apolloClient.query({
      query: PROJECT_ITEM,
      variables: { slug, locale },
    });

    const newData = flatten(data);

    if (locale !== (newData as { projects: Project[] })?.projects?.[0]?.locale) {
      return {
        notFound: true,
      };
    }
    const translatedObj = translateProperty(
      (newData as { projects: Project[] }).projects[0],
      ['limit', 'background', 'backgroundColor', 'content', 'cardBackground'],
      'projectsDynamic',
    );

    const newDz = orderDataByRichTextId(
      (translatedObj as { projectsDynamic: ProjectProjectsDynamicDynamicZone[] }).projectsDynamic,
      {
        page: `/projects/${slug}`,
        slug: slug,
        locale: locale,
      },
    );

    return {
      props: {
        project: { ...(translatedObj as Project), projectsDynamic: newDz },
        technologies: newData.technologiesData,
        testimonies: newData.testimonies,
        menus: {
          header: newData.header,
          technologies: newData.technologies,
          services: newData.services,
          footerPt: newData.footerPt,
          footerEn: newData.footerEn,
        },
      },
    };
  } catch (error) {
    sendError({ error: error as Error, slug, message: 'Error on projects/[slug]' });
    return {
      notFound: true,
    };
  }
};

export default ProjectPage;
